import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { CartItem, Product } from '../types';

const CART_STORAGE_KEY = 'yolo-cart';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- version marker for future migrations
const _CART_SCHEMA_VERSION = 2; // v1: brandInfo.name, v2: brandInfo.brandName

interface CartState {
  items: CartItem[];
}

type CartAction =
  | { type: 'add'; item: CartItem }
  | { type: 'remove'; identifier: string }
  | { type: 'update'; identifier: string; quantity: number }
  | { type: 'clear' }
  | { type: 'hydrate'; items: CartItem[] };

interface LegacyBrandInfo {
  brandId: number;
  name?: string;
  brandName?: string;
}

interface LegacyProduct {
  productIdentifier: string;
  name: string;
  brandInfo?: LegacyBrandInfo;
  price: number;
  availableToSell: number;
  [key: string]: unknown;
}

interface LegacyCartItem {
  product: LegacyProduct;
  quantity: number;
  size?: string;
}

/**
 * Migrate legacy cart items to current schema.
 * - v1 → v2: brandInfo.name → brandInfo.brandName
 */
const migrateCartItem = (item: LegacyCartItem): CartItem | null => {
  if (!item?.product?.productIdentifier) return null;

  const legacyBrand = item.product.brandInfo;
  const brandName = legacyBrand?.brandName ?? legacyBrand?.name ?? 'Unknown';

  // Treat missing availableToSell as unlimited (legacy items may not have this field)
  const availableToSell =
    typeof item.product.availableToSell === 'number' ? item.product.availableToSell : Infinity;

  const product: Product = {
    productIdentifier: item.product.productIdentifier,
    name: item.product.name ?? 'Unknown Product',
    brandInfo: legacyBrand
      ? { brandId: legacyBrand.brandId ?? 0, brandName }
      : undefined,
    price: item.product.price ?? 0,
    availableToSell,
    imageUrl: item.product.imageUrl as string | undefined,
    description: item.product.description as string | null | undefined,
    discount: item.product.discount as number | null | undefined,
    discountPercentage: item.product.discountPercentage as number | undefined,
    estimatedTimeOfArrival: item.product.estimatedTimeOfArrival as string | null | undefined,
    category: item.product.category as string | null | undefined,
    currencyCode: item.product.currencyCode as string | null | undefined,
    market: item.product.market as number | undefined,
  };

  return {
    product,
    quantity: item.quantity ?? 1,
    size: item.size,
  };
};

const loadCartFromStorage = (): CartItem[] => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];

      // Migrate each item
      const migrated = parsed
        .map((item: LegacyCartItem) => migrateCartItem(item))
        .filter((item): item is CartItem => item !== null);

      return migrated;
    }
  } catch {
    // Ignore storage errors
  }
  return [];
};

const saveCartToStorage = (items: CartItem[]): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Ignore storage errors
  }
};

const CartContext = createContext<{
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (identifier: string) => void;
  updateQuantity: (identifier: string, quantity: number) => void;
  clear: () => void;
} | null>(null);

/**
 * Clamp quantity to valid bounds: min 1, max availableToSell (if positive).
 * @param quantity - Requested quantity
 * @param availableToSell - Stock level (Infinity or undefined = unlimited, 0 = out of stock)
 * @param strict - If true, return 0 when out of stock. If false, preserve quantity (for hydration).
 */
const clampQuantity = (
  quantity: number,
  availableToSell?: number,
  strict = true,
): number => {
  const safeQuantity = Number.isFinite(quantity) ? quantity : 1;
  if (safeQuantity <= 0) return 0;

  // If out of stock and strict mode, return 0 (item will be removed)
  // In non-strict mode (hydration), keep the item even if out of stock
  if (strict && typeof availableToSell === 'number' && availableToSell <= 0) {
    return 0;
  }

  // Clamp to available stock if finite and positive
  const max =
    typeof availableToSell === 'number' && availableToSell > 0
      ? availableToSell
      : Infinity;
  return Math.max(1, Math.min(safeQuantity, max));
};

/**
 * Normalize items during hydration - preserves items even if out of stock.
 */
const normalizeItems = (items: CartItem[]): CartItem[] => {
  return items.reduce<CartItem[]>((acc, item) => {
    // Use non-strict mode during hydration to preserve cart items
    const clamped = clampQuantity(item.quantity, item.product.availableToSell, false);
    if (clamped <= 0) return acc;
    acc.push({ ...item, quantity: clamped });
    return acc;
  }, []);
};

function reducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'add': {
      const existing = state.items.find(
        (i) => i.product.productIdentifier === action.item.product.productIdentifier,
      );
      if (existing) {
        const newQuantity = existing.quantity + action.item.quantity;
        const clamped = clampQuantity(newQuantity, existing.product.availableToSell);
        if (clamped <= 0) {
          return {
            items: state.items.filter(
              (i) => i.product.productIdentifier !== action.item.product.productIdentifier,
            ),
          };
        }
        return {
          items: state.items.map((i) =>
            i.product.productIdentifier === action.item.product.productIdentifier
              ? { ...i, quantity: clamped }
              : i,
          ),
        };
      }
      const clamped = clampQuantity(action.item.quantity, action.item.product.availableToSell);
      if (clamped <= 0) {
        return state;
      }
      return { items: [...state.items, { ...action.item, quantity: clamped }] };
    }
    case 'remove':
      return {
        items: state.items.filter((i) => i.product.productIdentifier !== action.identifier),
      };
    case 'update': {
      const item = state.items.find((i) => i.product.productIdentifier === action.identifier);
      const clamped = clampQuantity(action.quantity, item?.product.availableToSell);
      if (clamped <= 0) {
        return {
          items: state.items.filter((i) => i.product.productIdentifier !== action.identifier),
        };
      }
      return {
        items: state.items.map((i) =>
          i.product.productIdentifier === action.identifier ? { ...i, quantity: clamped } : i,
        ),
      };
    }
    case 'clear':
      return { items: [] };
    case 'hydrate':
      return { items: normalizeItems(action.items) };
    default:
      return state;
  }
}

interface ProviderProps {
  children: React.ReactNode;
  initialItems?: CartItem[];
}

export const CartProvider = ({
  children,
  initialItems = [],
}: ProviderProps): React.ReactElement => {
  const [state, dispatch] = useReducer(reducer, { items: initialItems });

  // Hydrate cart from localStorage on mount
  useEffect(() => {
    const stored = loadCartFromStorage();
    if (stored.length > 0) {
      dispatch({ type: 'hydrate', items: stored });
    }
  }, []);

  // Persist cart to localStorage when items change
  useEffect(() => {
    saveCartToStorage(state.items);
  }, [state.items]);

  const addItem = React.useCallback((item: CartItem) => dispatch({ type: 'add', item }), []);
  const removeItem = React.useCallback(
    (identifier: string) => dispatch({ type: 'remove', identifier }),
    [],
  );
  const updateQuantity = React.useCallback(
    (identifier: string, quantity: number) => dispatch({ type: 'update', identifier, quantity }),
    [],
  );
  const clear = React.useCallback(() => dispatch({ type: 'clear' }), []);

  const contextValue = React.useMemo(
    () => ({
      items: state.items,
      addItem,
      removeItem,
      updateQuantity,
      clear,
    }),
    [state.items, addItem, removeItem, updateQuantity, clear],
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
